/** Reopen the stable, authorized resource URL after a long-lived player fails.
 * The browser obtains a fresh redirect; signed storage URLs never enter the
 * plugin API. An immediate failure is reported normally instead of retrying.
 */
export function installMediaRefresh(root, allowed) {
  const events = root.defaultView || root;
  const started = new WeakMap();
  const pending = new Map();
  const clock = () => Date.now();
  const isMedia = node => node && /^(IMG|VIDEO|AUDIO)$/.test(node.tagName);
  const loaded = event => {
    if (isMedia(event.target)) started.set(event.target, clock());
  };
  const failed = event => {
    const element = event.target;
    if (!isMedia(element)) return;
    if (pending.has(element)) {
      pending.get(element).clear();
    }
    const since = started.get(element);
    if (since === undefined || clock() - since < 60000) return;
    const source = element.hasAttribute("src") ? element : element.querySelector("source[src]");
    if (!source) return;
    let url;
    try { url = new URL(source.getAttribute("src"), root.baseURI); }
    catch { return; }
    if (!allowed(url)) return;
    started.set(element, clock());
    const position = Number(element.currentTime) || 0;
    let playing = element.paused === false && !root.hidden;
    let loadPause = playing;
    const originalPause = element.pause;
    const pauseDescriptor = Object.getOwnPropertyDescriptor(element, "pause");
    // load() pauses internally. A later explicit pause() is an intent even
    // when the player is already paused and the browser raises no pause event.
    const pause = function (...args) {
      playing = false;
      return originalPause.apply(this, args);
    };
    const paused = () => {
      if (loadPause) { loadPause = false; return; }
      playing = false;
    };
    const recovery = {
      cancel: () => { playing = false; },
      clear: () => {
        pending.delete(element);
        element.removeEventListener("loadedmetadata", restore);
        element.removeEventListener("pause", paused);
        if (element.pause === pause) {
          if (pauseDescriptor) Object.defineProperty(element, "pause", pauseDescriptor);
          else delete element.pause;
        }
      },
    };
    const restore = () => {
      recovery.clear();
      if (source.getAttribute("src") !== url.href) return;
      if (position && Number.isFinite(element.duration))
        element.currentTime = Math.min(position, Math.max(0, element.duration - 0.01));
      if (playing && !root.hidden && element.isConnected) element.play().catch(() => {});
    };
    // Let a failed refresh reach the usual error UI; never spin on a 403/404.
    event.stopImmediatePropagation();
    if (element.tagName !== "IMG") {
      try {
        Object.defineProperty(element, "pause", { configurable: true, writable: true, value: pause });
      } catch {
        // If the page owns a fixed pause method, safe recovery stays paused.
        playing = false;
      }
      pending.set(element, recovery);
      element.addEventListener("pause", paused);
      element.addEventListener("loadedmetadata", restore, { once: true });
    }
    url.searchParams.set("pa_media_refresh", String(clock()));
    source.setAttribute("src", url.href);
    if (element.tagName !== "IMG") {
      try { element.load(); }
      catch (error) { recovery.clear(); throw error; }
    }
  };
  const visibility = () => {
    if (root.hidden) pending.forEach(recovery => recovery.cancel());
  };
  root.addEventListener("visibilitychange", visibility);
  events.addEventListener("loadstart", loaded, true);
  events.addEventListener("load", loaded, true);
  events.addEventListener("error", failed, true);
  return () => {
    pending.forEach(recovery => recovery.clear());
    root.removeEventListener("visibilitychange", visibility);
    events.removeEventListener("loadstart", loaded, true);
    events.removeEventListener("load", loaded, true);
    events.removeEventListener("error", failed, true);
  };
}
